import { useMemo } from "react";
import { useField } from "formik";

import { FormGroup } from "@vega-core/components/Form/Layout/FormGroup";
import { FormRow } from "@vega-core/components/Form/Layout/FormRow";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { DropdownField } from "../Controls/Dropdown/Dropdown/DropdownField";

import { InputField } from "./InputField";

type Props = {
  name: string;
  honorificName?: string;
  inline?: boolean;
  title?: string;
  isOptional?: boolean;
  withMiddleName?: boolean;
  disabled?: boolean;
};

export function NameField({
  name,
  honorificName,
  inline,
  withMiddleName,
  disabled = false,
  title,
  isOptional,
}: Props) {
  const [firstNameField] = useField(`${name}.firstName`);
  const [lastNameField] = useField(`${name}.lastName`);
  const [middleNameField] = useField(`${name}.middleName`);

  const honorifics = useMemo(() => getHonorifics(), []);

  const firstNameInput = (
    <InputField
      disabled={disabled}
      isOptional={isOptional}
      label={m.formFields_firstName()}
      name={firstNameField.name}
    />
  );
  const middleNameInput = (
    <InputField
      disabled={disabled}
      isOptional
      label={m.formFields_middleName()}
      name={middleNameField.name}
    />
  );
  const lastNameInput = (
    <InputField
      disabled={disabled}
      isOptional={isOptional}
      label={m.formFields_lastName()}
      name={lastNameField.name}
    />
  );

  if (withMiddleName) {
    return (
      <FormGroup dataTestId="full-name">
        <div className="flex flex-col gap-2">
          {isTruthy(title) && (
            <span className="body-base-medium text-text/secondary">
              {title}
            </span>
          )}
          {firstNameInput}
          {middleNameInput}
          {lastNameInput}
        </div>
      </FormGroup>
    );
  }
  const honorificField = isTruthy(honorificName) && (
    <DropdownField
      label={m.formFields_salutation()}
      name={honorificName}
      options={honorifics}
    />
  );

  if (isTruthy(inline)) {
    return (
      <div className="flex flex-col gap-2">
        {isTruthy(title) && (
          <span className="body-base-medium text-text/secondary">{title}</span>
        )}
        <div className="flex gap-2 h-16">
          {isTruthy(honorificName) && (
            <div className="flex flex-col w-20 gap-1">{honorificField}</div>
          )}
          <div className="flex flex-col flex-1 gap-1">{firstNameInput}</div>
          <div className="flex flex-col flex-1 gap-1">{lastNameInput}</div>
        </div>
      </div>
    );
  }

  return isTruthy(honorificName) ? (
    <FormGroup dataTestId="honorificName-name">
      {isTruthy(title) && (
        <span className="body-base-medium text-text/secondary">{title}</span>
      )}
      <div className="flex gap-2">
        <div className="w-[150px]">{honorificField}</div>
        <div className="flex-1">{firstNameInput}</div>
      </div>
      {lastNameInput}
    </FormGroup>
  ) : (
    <div className="flex flex-col gap-2">
      {isTruthy(title) && (
        <span className="body-base-medium text-text/secondary">{title}</span>
      )}
      <FormRow>
        {firstNameInput}
        {lastNameInput}
      </FormRow>
    </div>
  );
}
export const HONORIFICS = [
  "-",
  "Mr",
  "Mrs",
  "Ms",
  "Dr",
  "Sir",
  "Dame",
  "Lord",
  "Lady",
];
export function getHonorifics(): OptionType[] {
  return HONORIFICS.map(
    (value) =>
      ({
        label: value,
        value: value,
      }) as OptionType,
  );
}
